import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  Heading,
  Input,
  Text,
  VStack
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { mockApi, type CreateCampaignData } from "../api/mockApi";

function CreateCampaign() {
  const [formData, setFormData] = useState({
    campaignName: "",
    adSetName: "",
    adName: "",
    platform: "",
    redirectUrl: ""
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createCampaignMutation = useMutation({
    mutationFn: (data: CreateCampaignData) => mockApi.createCampaign(data),
    onSuccess: (newCampaign) => {
      console.log("Campaign created:", newCampaign);
      // Invalidate and refetch links list
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate("/campaigns");
    },
    onError: (error) => {
      console.error("Campaign creation failed:", error);
    }
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const campaignData: CreateCampaignData = {
      campaignName: formData.campaignName,
      adSetName: formData.adSetName,
      adName: formData.adName,
      platform: formData.platform,
      redirectUrl: formData.redirectUrl
    };

    createCampaignMutation.mutate(campaignData);
  };

  // Platform options - USA/CA relevant platforms
  const platformOptions = [
    { value: "", label: "Select Platform" },
    { value: "Google Ads", label: "Google Ads" },
    { value: "YouTube Ads", label: "YouTube Ads" },
    { value: "Facebook Ads", label: "Facebook Ads" },
    { value: "Instagram Ads", label: "Instagram Ads" },
    { value: "TikTok Ads", label: "TikTok Ads" },
    { value: "Snapchat Ads", label: "Snapchat Ads" },
    { value: "LinkedIn Ads", label: "LinkedIn Ads" },
    { value: "X Ads (Twitter)", label: "X Ads (Twitter)" },
    { value: "Pinterest Ads", label: "Pinterest Ads" },
    { value: "Amazon Ads", label: "Amazon Ads" },
    { value: "Microsoft Ads", label: "Microsoft Ads (Bing)" },
    { value: "Reddit Ads", label: "Reddit Ads" },
    { value: "Custom", label: "Custom" },
    { value: "Other", label: "Other" }
  ];

  return (
    <Container maxW="4xl" py={8}>
      <Box mb={6}>
        <Link to="/campaigns">
          <Text color="blue.500" fontWeight="medium" textDecoration="underline">
            ← Back to Campaigns
          </Text>
        </Link>
      </Box>

      <Heading size="xl" mb={8} color="gray.800">
        Create New Campaign
      </Heading>

      <Box bg="white" rounded="lg" shadow="md" p={8}>
        <Box as="form" onSubmit={handleSubmit}>
          <VStack gap={6} align="stretch">
            <Box>
              <Text mb={2} fontWeight="medium">
                Platform *
              </Text>
              <Box>
                <select
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  disabled={createCampaignMutation.isPending}
                  required
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    border: "1px solid #E2E8F0",
                    borderRadius: "0.375rem",
                    fontSize: "1rem",
                    backgroundColor: "white"
                  }}
                >
                  {platformOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Box>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Choose the advertising platform for this campaign
              </Text>
            </Box>

            <Box>
              <Text mb={2} fontWeight="medium">
                Campaign Name (Optional)
              </Text>
              <Input
                name="campaignName"
                value={formData.campaignName}
                onChange={handleChange}
                placeholder="Enter campaign name"
                disabled={createCampaignMutation.isPending}
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                A descriptive name for your advertising campaign
              </Text>
            </Box>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <Text mb={2} fontWeight="medium">
                  Ad Set Name (Optional)
                </Text>
                <Input
                  name="adSetName"
                  value={formData.adSetName}
                  onChange={handleChange}
                  placeholder="Enter ad set name"
                  disabled={createCampaignMutation.isPending}
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Target audience group name
                </Text>
              </GridItem>

              <GridItem>
                <Text mb={2} fontWeight="medium">
                  Ad Name (Optional)
                </Text>
                <Input
                  name="adName"
                  value={formData.adName}
                  onChange={handleChange}
                  placeholder="Enter ad name"
                  disabled={createCampaignMutation.isPending}
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Individual ad creative name
                </Text>
              </GridItem>
            </Grid>

            <Box>
              <Text mb={2} fontWeight="medium">
                Redirect URL *
              </Text>
              <Input
                name="redirectUrl"
                type="url"
                value={formData.redirectUrl}
                onChange={handleChange}
                placeholder="https://example.com/landing-page"
                disabled={createCampaignMutation.isPending}
                required
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                The URL where users will be redirected when they click on your
                ad
              </Text>
            </Box>

            {createCampaignMutation.error && (
              <Box p={3} bg="red.50" color="red.600" rounded="md">
                {createCampaignMutation.error.message}
              </Box>
            )}

            <Box display="flex" gap={4} justifyContent="flex-end" pt={4}>
              <Link to="/campaigns">
                <Button
                  variant="outline"
                  disabled={createCampaignMutation.isPending}
                >
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                colorPalette="green"
                loading={createCampaignMutation.isPending}
              >
                Create Campaign
              </Button>
            </Box>
          </VStack>
        </Box>
      </Box>
    </Container>
  );
}

export default CreateCampaign;
